/**
 * Every lift car's state for the active site, kept current without new
 * data (twin slice 1, W6 step 9): the one hook the drawer's reading
 * lines, the 2D pictogram, the 3D columns and the attention list read.
 * The rules are in `lift-column-state.ts`; this file wires them to the
 * page's clocks, the site's feed health and its time zone.
 *
 * It recomputes on every render -- six cars -- and the tracker hands
 * back the SAME map when nothing a surface shows changed. What makes it
 * render without new data is the boundary tick: the soonest moment any
 * car's freshness would flip (checking -> lost, live -> quiet -> lost).
 * A minute tick re-reads the wall clock, which only names the day of a
 * "since HH:MM" (`formatClock`), so "yesterday" rolls over at midnight.
 *
 * THE CAR LIST IS AN INPUT. The building's cars and their robot facts
 * come from W1's asset (`liftFactsFromAsset(twinAssetFor(site).lifts)`),
 * which no SPA surface imports yet; the server's robot roster (`GET
 * .../lifts/robot-roster`, K3) names only the robot cars and carries no
 * map lanes, so it cannot give all six or the FL1/FL2 ops line. Every
 * car in the list is present in the result, with or without a live row.
 */
import { useEffect, useRef } from 'react';
import type { LiftViewModel } from '../../api/adapters';
import { useFeedHealth } from '../../api/feed-health';
import type { LiftWriteGate } from '../../api/v1';
import { monotonicNow, useBoundaryTick, useMinuteTick } from '../../state/clock';
import { useActiveSite } from '../../state/useActiveSite';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import {
  LiftColumnTracker,
  liftReceiptsFor,
  liftsNextChangeMs,
  observeLiftRows,
  type LiftCarView,
  type LiftColumnState,
  type LiftReceiptBook,
} from './lift-column-state';
import type { LiftRobotFacts } from './lift-reading';

export interface LiftColumnStateInput {
  /** Every car the site has, with its building facts. */
  readonly cars: readonly LiftRobotFacts[];
  /** The live rows (`useLifts().data`). */
  readonly lifts: readonly LiftViewModel[] | undefined;
  /** The site's lift write gate (`useLiftWriteGate().data`). */
  readonly gate: LiftWriteGate | null | undefined;
}

/** The site's book, held while this mount lives, and one tracker per
 *  site so identities never cross sites. */
function useSiteBook(): { siteId: string; book: LiftReceiptBook; tracker: LiftColumnTracker } {
  const { site } = useActiveSite();
  const siteId = site?.id ?? '';
  const book = liftReceiptsFor(siteId);
  useEffect(() => book.hold(), [book]);
  const tracker = useRef<{ site: string; t: LiftColumnTracker } | null>(null);
  if (!tracker.current || tracker.current.site !== siteId) {
    tracker.current = { site: siteId, t: new LiftColumnTracker() };
  }
  return { siteId, book, tracker: tracker.current.t };
}

/** Every car in `cars`, keyed by name, with its freshness, look and
 *  reading. */
export function useLiftColumnState({
  cars,
  lifts,
  gate,
}: LiftColumnStateInput): ReadonlyMap<string, LiftColumnState> {
  const { sites } = useActiveSite();
  const { siteId, book, tracker } = useSiteBook();
  const feed = useFeedHealth(siteId);
  const wallNowMs = useMinuteTick();
  const nowMs = monotonicNow();
  observeLiftRows(book, lifts, feed, nowMs);
  const zone = resolveSiteTimeZone(sites, siteId);
  const columns = tracker.columns({
    cars,
    rows: lifts,
    book,
    nowMs,
    feed,
    gate,
    // The device's zone only says "(device time)" when it is a guess.
    timeZone: zone.source === 'site' ? zone.timeZone : null,
    wallNowMs,
  });
  useBoundaryTick(
    liftsNextChangeMs(
      cars.map((c) => c.name),
      book,
      nowMs,
      feed,
    ),
  );
  return columns;
}

/**
 * Freshness and looks only, for cars known by name: a surface with no
 * building facts (the 2D map until its host passes the column state).
 * Reads the same book as `useLiftColumnState`, so the two agree.
 */
export function useLiftCarViews(
  names: readonly string[],
  lifts: readonly LiftViewModel[] | undefined,
): ReadonlyMap<string, LiftCarView> {
  const { siteId, book, tracker } = useSiteBook();
  const feed = useFeedHealth(siteId);
  const nowMs = monotonicNow();
  observeLiftRows(book, lifts, feed, nowMs);
  const views = tracker.views({ names, rows: lifts, book, nowMs, feed });
  useBoundaryTick(liftsNextChangeMs(names, book, nowMs, feed));
  return views;
}
